import { Box, Grid2, Typography } from "@mui/material";
import { format } from "date-fns";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { WorkerReassignmentList } from "../../../../store/full-time-shifts-type";
import { ReviewScheduleDialogCalendarHeader } from "./review-schedule-dialog-calendar-header";
import { WorkerWorkcenterGroup } from "./worker-workcenter-group";

type GroupedReassignments = Record<
  number, // workCenterId
  Record<
    number, // patternId
    Record<
      string, // workerId
      {
        workerInfo: any;
        shiftsByDay: Record<string, any>; // dateString → reassignment
      }
    >
  >
>;

const groupReassignments = (reassignments: any[]): GroupedReassignments => {
  const grouped: GroupedReassignments = {};

  for (const reassignment of reassignments) {
    const workCenterId = reassignment.from.workCenterId;
    const patternId = reassignment.from.patternId;
    const workerId = reassignment.workerId;
    const dateKey = format(new Date(reassignment.startDate), "yyyy-MM-dd");

    if (!grouped[workCenterId]) grouped[workCenterId] = {};
    if (!grouped[workCenterId][patternId])
      grouped[workCenterId][patternId] = {};
    if (!grouped[workCenterId][patternId][workerId]) {
      grouped[workCenterId][patternId][workerId] = {
        workerInfo: reassignment,
        shiftsByDay: {},
      };
    }

    // Add shift only if no other shift already exists for this day
    if (!grouped[workCenterId][patternId][workerId].shiftsByDay[dateKey]) {
      grouped[workCenterId][patternId][workerId].shiftsByDay[dateKey] =
        reassignment;
    }
  }

  return grouped;
};

type TotalWorkerMovementProps = {
  startingDay: Date;
  workerReassignments: WorkerReassignmentList;
};
export const TotalWorkerMovement = ({
  startingDay,
  workerReassignments,
}: TotalWorkerMovementProps) => {
  const { t } = useTranslation();
  const [searchedTerm, setSearchTerm] = React.useState<string>("");
  const [filteredWorkerReassignments, setFilteredWorkerReassignments] =
    React.useState<WorkerReassignmentList>([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchedTerm) {
        const filtered = workerReassignments.filter((reassignment) =>
          `${reassignment.workerFirstName?.[0]}${reassignment.workerLastName?.[0]} ${reassignment.workerFirstName} ${reassignment.workerLastName} ${reassignment.workerTypeName} (${reassignment.workerSubTypeName}) ${reassignment.from.jobName} ${reassignment.from.patternName} ${reassignment.from.workCenterName} ${reassignment.badgeNumber} ${reassignment.companyEmployeeId}`
            .toLowerCase()
            .includes(searchedTerm.toLowerCase())
        );
        setFilteredWorkerReassignments(filtered);
      } else {
        setFilteredWorkerReassignments(workerReassignments ?? []);
      }
    }, 600); // 600ms debounce delay

    return () => {
      clearTimeout(handler); // Cleanup timeout on unmount or dependency change
    };
  }, [searchedTerm, workerReassignments]);

  useEffect(() => {
    setSearchTerm("");
  }, []);

  return (
    <Grid2
      container
      sx={
        filteredWorkerReassignments.length === 0
          ? {
              flexGrow: 1,
              width: "100%",
              height: "100%",
            }
          : {
              flexGrow: 1,
            }
      }
    >
      <ReviewScheduleDialogCalendarHeader
        searchedTerm={searchedTerm}
        startingDay={startingDay}
        setSearchTerm={(term: string) => setSearchTerm(term)}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "calc(100% - 64px)",
        }}
      >
        {Object.entries(groupReassignments(filteredWorkerReassignments)).map(
          ([workcenterId, patternsByPatternId]) => (
            <WorkerWorkcenterGroup
              key={`workcenter-${workcenterId}`}
              patterns={patternsByPatternId}
              searchedTerm={searchedTerm}
              startingDay={startingDay}
            />
          )
        )}
        {workerReassignments.length !== 0 && filteredWorkerReassignments.length === 0 && searchedTerm !== "" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "rgba(0,0,0,0.6)",
                textAlign: "center",
              }}
            >
              {t("full-time-shifts_no-results-found")}
              <br />
              {t("full-time-shifts_adjust-search-terms")}
            </Typography>
          </Box>
        )}
        {workerReassignments.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontSize: "16px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "rgba(0,0,0,0.6)",
                textAlign: "center",
              }}
            >
              {t("full-time-shifts_no-movements-title")}
              <br />
              {t("full-time-shifts_no-movements-subtitle")}
            </Typography>
          </Box>
        )}
      </Box>
    </Grid2>
  );
};
